/**
 * Edit Property page — thin wrapper around the shared PropertyForm (Task 3.1).
 *
 * This page owns only the edit-specific responsibilities:
 *   - auth + ownership guard and redirects,
 *   - loading the property (GET /properties/:id) and mapping it to form state,
 *   - the submit orchestration: upload NEW image files, reconstruct the final
 *     ordered gallery (kept existing URLs + newly uploaded ones), apply the
 *     cover ordering + floor-plan tagging, and PUT /properties/:id.
 *
 * The form UI itself lives in PropertyForm; this file no longer duplicates it.
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navigation from '../../../components/feature/Navigation';
import Footer from '../../../components/feature/Footer';
import Button from '../../../components/base/Button';
import PropertyForm, { type PropertyFormSubmit } from '../../../components/property/PropertyForm';
import { useAuth } from '../../../hooks/useAuth';
import { apiRequest, ApiError } from '../../../utils/apiClient';
import {
  type FormState,
  formStateToPayload,
  mapServerErrorToFields,
  propertyToFormState,
  propertyToImageUrls,
  propertyToFloorPlanUrls,
} from '../../../utils/propertyFormState';
import { uploadNewImages } from '../../../utils/uploadPropertyImages';

// A preview that is a stored/uploaded image (http/https) rather than a
// not-yet-uploaded new file (data:/blob:). Used to walk the displayed gallery
// and decide which previews to keep as-is vs. swap for an uploaded URL.
const isStoredUrl = (url: string): boolean => !url.startsWith('data:') && !url.startsWith('blob:');

export default function EditPropertyPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, session, loading: authLoading } = useAuth();
  const { t, i18n } = useTranslation(['propertyCreate', 'search', 'common']);
  const dir = i18n.dir();
  const lang = i18n.resolvedLanguage ?? 'en';
  const isRTL = dir === 'rtl';

  // Prefill produced from the loaded property (null until the GET resolves).
  const [initialForm, setInitialForm] = useState<FormState | null>(null);
  const [initialImageUrls, setInitialImageUrls] = useState<string[]>([]);
  // Existing floor-plan URLs (re-tagged on save if still present in the gallery).
  const [initialFloorPlanUrls, setInitialFloorPlanUrls] = useState<string[]>([]);

  const [uploadProgress, setUploadProgress] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [serverFieldErrors, setServerFieldErrors] = useState<Record<string, string>>({});
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Load property data + ownership/permission guard (unchanged from before).
  useEffect(() => {
    if (!id || authLoading) return;

    if (!user) {
      navigate('/signin?redirect=/property/' + id + '/edit');
      return;
    }

    const fetchProperty = async () => {
      try {
        setLoading(true);
        setLoadError(null);

        const response = await apiRequest<{ data?: Record<string, unknown> } & Record<string, unknown>>(
          `/properties/${id}`,
          { token: session?.token },
        );

        // Handle both wrapped ({data: ...}) and unwrapped API responses.
        const property = ((response as { data?: Record<string, unknown> }).data ?? response) as Record<string, unknown>;

        if (!property || !property.id) {
          throw new Error('Invalid property data received');
        }

        // Map the API record → form state + existing gallery/floor-plan URLs.
        // propertyToFormState accepts both web (camelCase) and mobile
        // (snake_case) response shapes and converts DB sqft → form m².
        setInitialForm(propertyToFormState(property));
        setInitialImageUrls(propertyToImageUrls(property));
        setInitialFloorPlanUrls(propertyToFloorPlanUrls(property));
      } catch (error) {
        console.error('Failed to load property:', error);
        if (error instanceof ApiError && error.status === 404) {
          setLoadError(isRTL ? 'العقار غير موجود' : 'Property not found');
        } else if (error instanceof ApiError && error.status === 403) {
          setLoadError(isRTL ? 'ليس لديك صلاحية لتعديل هذا العقار' : 'You do not have permission to edit this property');
        } else {
          setLoadError(isRTL ? 'فشل في تحميل بيانات العقار' : 'Failed to load property data');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProperty();
  }, [id, user, session, authLoading, navigate, isRTL]);

  // Submit orchestration. PropertyForm has already validated; here we own image
  // upload, final-gallery reconstruction, payload assembly, and the PUT.
  const handleUpdate = async (data: PropertyFormSubmit) => {
    if (submitting) return;

    const { form, imageFiles, coverImageIndex, floorPlanIndices, imagePreviews } = data;
    setSubmitting(true);
    setSubmissionError(null);
    setServerFieldErrors({});
    setSuccessMessage(null);
    setUploadProgress(null);

    try {
      // 1. Upload the NEW files (those whose preview is a data:/blob: URL),
      //    keeping upload order. Abort on any failure.
      let uploadedUrls: string[] = [];
      if (imageFiles.length > 0) {
        const uploaded = await uploadNewImages(imageFiles, session?.token, {
          onProgress: (current, total) =>
            setUploadProgress(
              isRTL ? `جارٍ رفع الصور... (${current}/${total})` : `Uploading images... (${current}/${total})`,
            ),
        });
        if (!uploaded) {
          const msg = isRTL
            ? 'تعذّر رفع بعض الصور. يرجى إزالتها والمحاولة مرة أخرى.'
            : 'Some images failed to upload. Please remove them and try again.';
          setServerFieldErrors({ _form: msg });
          setSubmissionError(msg);
          setUploadProgress(null);
          setSubmitting(false);
          return;
        }
        uploadedUrls = uploaded.uploadedUrls;
      }

      setUploadProgress(isRTL ? 'جارٍ حفظ التغييرات...' : 'Saving changes...');

      // 2. Reconstruct the FINAL ordered gallery by walking imagePreviews in
      //    display order: keep stored URLs as-is; replace each new-file preview
      //    (data:/blob:) with the next uploaded URL, consumed in order. Removed
      //    images are already absent from imagePreviews.
      let uploadCursor = 0;
      const finalUrls: string[] = [];
      for (const preview of imagePreviews) {
        if (isStoredUrl(preview)) {
          finalUrls.push(preview);
        } else if (uploadCursor < uploadedUrls.length) {
          finalUrls.push(uploadedUrls[uploadCursor]);
          uploadCursor += 1;
        }
        // (a new-file preview with no matching upload was already aborted above)
      }

      // 3. Apply cover ordering over the FINAL list: move coverImageIndex to front.
      const safeCoverIndex = Math.min(coverImageIndex, finalUrls.length - 1);
      const orderedUrls = finalUrls.length > 0 && safeCoverIndex >= 0
        ? [finalUrls[safeCoverIndex], ...finalUrls.filter((_, i) => i !== safeCoverIndex)]
        : finalUrls;

      // 4. Compute floor-plan URLs: the URLs whose index (into the same ordered
      //    display list, before cover-reorder) was tagged in the form. The form
      //    is the single source of truth - it was pre-seeded with the
      //    previously-saved floor plans (initialFloorPlanUrls) so untagging
      //    works too. Emit them in final gallery order.
      const taggedFloorPlanUrls = new Set<string>();
      finalUrls.forEach((url, i) => {
        if (floorPlanIndices.has(i)) taggedFloorPlanUrls.add(url);
      });
      const floorPlanUrls = orderedUrls.filter((url) => taggedFloorPlanUrls.has(url));

      // 5. Build the payload via the shared mapper, then layer the edit-only
      //    fields the backend update accepts (status + floor/unit detail). This
      //    keeps the PUT body identical to the previous edit page.
      const payload = {
        ...formStateToPayload(form, orderedUrls, floorPlanUrls),
        floor: form.floor ? Number(form.floor) : undefined,
        totalFloors: form.totalFloors ? Number(form.totalFloors) : undefined,
        apartmentNumber: form.unitNumber.trim() || undefined,
        floorLevel: form.floor ? Number(form.floor) : undefined,
        videoUrl: form.videoTourUrl.trim() || null,
        status: form.status,
      };

      // 6. Persist. The edit endpoint is a PUT (matches the existing backend).
      await apiRequest(`/properties/${id}`, {
        method: 'PUT',
        body: payload,
        token: session?.token,
      });

      // Re-seed the gallery from the persisted order so the form reflects what
      // was saved (cover first), and remember the new floor-plan set.
      setInitialImageUrls(orderedUrls);
      setInitialFloorPlanUrls(floorPlanUrls);
      setSuccessMessage(isRTL ? 'تم حفظ التغييرات بنجاح!' : 'Changes saved successfully!');
      setUploadProgress(null);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Failed to update property:', error);
      setUploadProgress(null);
      if (error instanceof ApiError) {
        const errorBody = error.details as { message?: string; details?: Array<{ path?: (string | number)[]; message: string }> } | undefined;
        const fieldErrors = mapServerErrorToFields(errorBody ?? { message: error.message });
        setServerFieldErrors(fieldErrors);
        const generalMsg = fieldErrors._form
          ?? errorBody?.message
          ?? error.message
          ?? (isRTL ? 'فشل في تحديث العقار' : 'Failed to update property');
        setSubmissionError(generalMsg);
      } else if (error instanceof Error) {
        setSubmissionError(error.message);
      } else {
        setSubmissionError(isRTL ? 'فشل في تحديث العقار' : 'Failed to update property');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state (auth or property fetch in flight).
  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-white" dir={dir} lang={lang}>
        <Navigation />
        <main className="mx-auto max-w-5xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <i className="ri-loader-4-line text-4xl text-[#004D40] animate-spin"></i>
              <p className="mt-4 text-gray-600">{t('common:common.loading')}</p>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Load/permission error state.
  if (loadError || !initialForm) {
    return (
      <div className="min-h-screen bg-white" dir={dir} lang={lang}>
        <Navigation />
        <main className="mx-auto max-w-5xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-center min-h-[400px]">
            <i className="ri-error-warning-line text-6xl text-red-500 mb-4"></i>
            <h1 className="text-2xl font-bold text-[var(--pv-color-primary)] mb-2">{isRTL ? 'خطأ' : 'Error'}</h1>
            <p className="text-gray-600 mb-6">{loadError ?? (isRTL ? 'فشل في تحميل بيانات العقار' : 'Failed to load property data')}</p>
            <Button onClick={() => navigate(-1)}>
              <i className="ri-arrow-left-line mr-2"></i>
              {isRTL ? 'العودة' : 'Go Back'}
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" dir={dir} lang={lang}>
      <Navigation />
      <main className="mx-auto max-w-5xl px-4 py-12 sm:px-6 lg:px-8">
        <header className="mb-10">
          <div className="flex items-center gap-4 mb-4">
            <button
              type="button"
              onClick={() => navigate(`/property/${id}`)}
              className="flex items-center gap-2 text-sm font-medium text-[#004D40] hover:text-[#003930]"
            >
              <i className={`ri-arrow-${isRTL ? 'right' : 'left'}-line`}></i>
              {isRTL ? 'العودة للعقار' : 'Back to Property'}
            </button>
          </div>
          <p className="inline-flex items-center rounded-full bg-amber-100 px-3 py-1 text-sm font-semibold text-amber-700">
            <i className="ri-edit-line mr-2"></i>
            {isRTL ? 'تعديل العقار' : 'Edit Property'}
          </p>
          <h1 className="mt-4 text-3xl font-bold text-[var(--pv-color-primary)]">
            {isRTL ? 'تعديل بيانات العقار' : 'Edit Property Details'}
          </h1>
          <p className="mt-2 text-gray-600">
            {isRTL ? 'قم بتحديث بيانات عقارك' : 'Update your property information'}
          </p>

          {successMessage && (
            <div className="mt-6 rounded-lg border border-green-200 bg-green-50 p-4 text-green-900">
              <div className="flex items-center gap-2">
                <i className="ri-checkbox-circle-fill text-xl text-green-600"></i>
                <span>{successMessage}</span>
              </div>
              <Button size="sm" className="mt-3" onClick={() => navigate(`/property/${id}`)}>
                {isRTL ? 'عرض العقار' : 'View Property'}
              </Button>
            </div>
          )}

          {submissionError && (
            <div className="mt-6 rounded-lg border border-red-200 bg-red-50 p-4 text-red-900">
              <div className="flex items-center gap-2">
                <i className="ri-error-warning-fill text-xl text-red-600"></i>
                <span className="whitespace-pre-wrap">{submissionError}</span>
              </div>
            </div>
          )}
        </header>

        <PropertyForm
          variant="edit"
          initialForm={initialForm}
          initialImageUrls={initialImageUrls}
          initialFloorPlanUrls={initialFloorPlanUrls}
          showListUnder={false}
          submitting={submitting}
          serverErrors={Object.keys(serverFieldErrors).length ? serverFieldErrors : undefined}
          submitLabel={isRTL ? 'حفظ التغييرات' : 'Save Changes'}
          uploadProgress={uploadProgress}
          onSubmit={handleUpdate}
        />
      </main>
      <Footer />
    </div>
  );
}
